import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Sparkle, FloppyDisk, UploadSimple, ClipboardText, Plus, Paperclip, ArrowRight,
  ArrowLeft, CheckCircle, Trash, MagnifyingGlass, DownloadSimple, ClockCounterClockwise, FlagBanner, LinkSimple,
} from "@phosphor-icons/react";

const STAGES = [
  { key: "draft", label: "Draft / Intake" },
  { key: "sme_input", label: "SME Input" },
  { key: "compliance_review", label: "Compliance Review" },
  { key: "other_legal_review", label: "Other / Legal" },
  { key: "compliance_final", label: "Compliance (Final)" },
  { key: "complete", label: "Complete" },
];
const STAGE_CLS = {
  draft: "bg-secondary text-gray-700 border-gray-300",
  sme_input: "bg-blue-50 text-blue-700 border-blue-300",
  compliance_review: "bg-amber-50 text-amber-700 border-amber-300",
  other_legal_review: "bg-purple-50 text-purple-700 border-purple-300",
  compliance_final: "bg-amber-50 text-amber-800 border-amber-400",
  complete: "bg-green-50 text-green-700 border-green-300",
};
const stageLabel = (k) => (STAGES.find((s) => s.key === k) || {}).label || k;

function StageBadge({ status, ...rest }) {
  return (
    <span {...rest} className={`inline-block border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STAGE_CLS[status] || STAGE_CLS.draft}`}>
      {stageLabel(status)}
    </span>
  );
}

/* ---------------------------------- Intake --------------------------------- */
function IntakePanel({ standards, onCreated }) {
  const [mode, setMode] = useState("paste"); // paste | word
  const [text, setText] = useState("");
  const [sel, setSel] = useState("");
  const [std, setStd] = useState("none");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submitText = async () => {
    if (!text.trim()) return toast.error("Paste the RFI questions first");
    setBusy(true);
    try {
      const { data } = await api.post("/rfis/intake-text", {
        text, sel_number: sel, standard_id: std === "none" ? null : std,
      });
      toast.success(`${data.created} RFI item(s) created`);
      setText(""); setSel("");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitWord = async () => {
    if (!file) return toast.error("Choose a Word .docx file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sel_number", sel);
      fd.append("standard_id", std === "none" ? "" : std);
      const { data } = await api.post("/rfis/intake-docx", fd);
      toast.success(`${data.created} RFI item(s) created`);
      setFile(null); setSel("");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-5" data-testid="rfi-intake-panel">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardText size={20} weight="bold" />
        <h2 className="font-display font-bold text-lg">Intake RFI</h2>
      </div>
      <div className="flex gap-2 mb-4">
        <Button size="sm" variant={mode === "paste" ? "default" : "outline"} data-testid="intake-mode-paste" onClick={() => setMode("paste")}>Paste text (Align)</Button>
        <Button size="sm" variant={mode === "word" ? "default" : "outline"} data-testid="intake-mode-word" onClick={() => setMode("word")}>Upload Word</Button>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-xs">SEL # (request / self-report number)</Label>
          <Input data-testid="intake-sel" value={sel} onChange={(e) => setSel(e.target.value)} placeholder="e.g. SEL-2026-014" />
        </div>
        <div>
          <Label className="text-xs">Default Standard (optional)</Label>
          <Select value={std} onValueChange={setStd}>
            <SelectTrigger data-testid="intake-standard"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Auto-detect</SelectItem>
              {standards.map((s) => <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {mode === "paste" ? (
        <>
          <Textarea data-testid="intake-text" rows={5} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={"Paste questions copied from the NERC Align tool — one question per line."} />
          <div className="mt-3">
            <Button data-testid="intake-text-submit" onClick={submitText} disabled={busy}>
              <Plus size={16} className="mr-1" />{busy ? "Creating…" : "Create RFI items"}
            </Button>
          </div>
        </>
      ) : (
        <>
          <input data-testid="intake-word-file" type="file" accept=".docx"
            onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          <div className="mt-3">
            <Button data-testid="intake-word-submit" onClick={submitWord} disabled={busy}>
              <UploadSimple size={16} className="mr-1" />{busy ? "Parsing…" : "Upload & create"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------- RFI Detail -------------------------------- */
function RfiDetail({ rfiId, standards, onClose, onChanged }) {
  const { isRole, aiEnabled } = useAuth();
  const canEdit = isRole("admin") || isRole("analyst") || isRole("reviewer");
  const [rfi, setRfi] = useState(null);
  const [form, setForm] = useState({});
  const [aiBusy, setAiBusy] = useState(false);
  const [actComment, setActComment] = useState("");
  const [actAssignee, setActAssignee] = useState("");
  const [attLink, setAttLink] = useState("");

  const load = useCallback(() => {
    api.get(`/rfis/${rfiId}`).then((r) => { setRfi(r.data); setForm(r.data); }).catch(() => {});
  }, [rfiId]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await api.put(`/rfis/${rfiId}`, {
        sel_number: form.sel_number, standard_id: form.standard_id || null, req_part: form.req_part,
        question: form.question, narrative: form.narrative, notes: form.notes,
        assigned_sme: form.assigned_sme, assigned_compliance: form.assigned_compliance,
        assigned_other_legal: form.assigned_other_legal,
      });
      toast.success("Saved");
      onChanged(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const aiAnswer = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/rfis/${rfiId}/ai-answer`);
      setRfi((r) => ({ ...r, ai_suggested: data.ai_suggested }));
      toast.success("AI answer drafted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const doAction = async (action) => {
    try {
      await api.post(`/rfis/${rfiId}/action`, { action, comment: actComment, assignee: actAssignee });
      toast.success("Workflow updated");
      setActComment(""); setActAssignee("");
      onChanged(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/rfis/${rfiId}/attachments`, fd); toast.success("Attached"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addLink = async () => {
    if (!/^https?:\/\//i.test(attLink.trim())) return toast.error("Enter a valid http(s):// link");
    try { await api.post(`/rfis/${rfiId}/links`, { url: attLink.trim() }); toast.success("Link added"); setAttLink(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAtt = async (aid) => {
    try { await api.delete(`/rfis/${rfiId}/attachments/${aid}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rfi) return null;
  const status = rfi.status;
  const canForward = status !== "complete";
  const canReturn = status !== "draft" && status !== "complete";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rfi-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 pr-6">
            <span className="truncate">{rfi.title || "RFI"}</span>
            <StageBadge status={status} data-testid="rfi-detail-status" />
          </DialogTitle>
          <DialogDescription className="sr-only">Edit RFI details, run the workflow, add attachments and AI-draft an answer.</DialogDescription>
        </DialogHeader>

        {/* stepper */}
        <div className="flex items-center gap-1 flex-wrap text-[10px] font-semibold uppercase">
          {STAGES.map((s, i) => {
            const idx = STAGES.findIndex((x) => x.key === status);
            const done = i < idx, cur = i === idx;
            return (
              <span key={s.key} className={`px-2 py-0.5 border rounded-sm ${cur ? STAGE_CLS[s.key] : done ? "bg-green-50 text-green-600 border-green-200" : "bg-secondary text-gray-400 border-gray-200"}`}>{s.label}</span>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">SEL #</Label>
            <Input data-testid="rfi-sel" value={form.sel_number || ""} onChange={(e) => setField("sel_number", e.target.value)} disabled={!canEdit} /></div>
          <div><Label className="text-xs">Standard</Label>
            <Select value={form.standard_id || "none"} onValueChange={(v) => setField("standard_id", v === "none" ? null : v)} disabled={!canEdit}>
              <SelectTrigger data-testid="rfi-standard"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {form.standard_id && !standards.some((s) => s.standard_id === form.standard_id) && (
                  <SelectItem value={form.standard_id}>{form.standard_id}</SelectItem>
                )}
                {standards.map((s) => <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs">Req / Part</Label>
            <Input data-testid="rfi-reqpart" value={form.req_part || ""} onChange={(e) => setField("req_part", e.target.value)} disabled={!canEdit} /></div>
        </div>

        <div><Label className="text-xs">Question</Label>
          <Textarea data-testid="rfi-question" rows={3} value={form.question || ""} onChange={(e) => setField("question", e.target.value)} disabled={!canEdit} /></div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Answer / Narrative</Label>
            {canEdit && (
              <Button size="sm" variant="outline" data-testid="rfi-ai-answer" onClick={aiAnswer} disabled={aiBusy || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined}>
                <Sparkle size={14} className="mr-1" />{aiBusy ? "Drafting…" : "AI answer"}
              </Button>
            )}
          </div>
          <Textarea data-testid="rfi-narrative" rows={5} value={form.narrative || ""} onChange={(e) => setField("narrative", e.target.value)} disabled={!canEdit} />
        </div>

        {rfi.ai_suggested && (
          <div className="border border-purple-200 bg-purple-50 p-3 text-sm" data-testid="rfi-ai-suggested">
            <div className="flex items-center justify-between mb-1">
              <span className="overline text-purple-700">AI suggested answer</span>
              {canEdit && <Button size="sm" variant="ghost" data-testid="rfi-use-ai" onClick={() => setField("narrative", rfi.ai_suggested)}>Use as answer</Button>}
            </div>
            <p className="whitespace-pre-wrap text-purple-900/90">{rfi.ai_suggested}</p>
          </div>
        )}

        {/* assignees */}
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">SME</Label>
            <Input data-testid="rfi-assigned-sme" value={form.assigned_sme || ""} onChange={(e) => setField("assigned_sme", e.target.value)} disabled={!canEdit} placeholder="Name" /></div>
          <div><Label className="text-xs">Compliance</Label>
            <Input data-testid="rfi-assigned-compliance" value={form.assigned_compliance || ""} onChange={(e) => setField("assigned_compliance", e.target.value)} disabled={!canEdit} placeholder="Name" /></div>
          <div><Label className="text-xs">Other / Legal (named person)</Label>
            <Input data-testid="rfi-assigned-legal" value={form.assigned_other_legal || ""} onChange={(e) => setField("assigned_other_legal", e.target.value)} disabled={!canEdit} placeholder="Name" /></div>
        </div>

        <div><Label className="text-xs">Notes</Label>
          <Textarea data-testid="rfi-notes" rows={2} value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} disabled={!canEdit} /></div>

        {/* attachments */}
        <div className="border border-border p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="overline flex items-center gap-1"><Paperclip size={14} /> Attachments ({(rfi.attachments || []).length})</span>
            <label className="text-xs text-blue-700 cursor-pointer hover:underline" data-testid="rfi-attach-label">
              + Add file
              <input data-testid="rfi-attach-input" type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          </div>
          <div className="flex gap-2 mb-2">
            <Input data-testid="rfi-link-input" value={attLink} onChange={(e) => setAttLink(e.target.value)}
              placeholder="…or paste a repository link (https://…)" className="text-sm" />
            <Button size="sm" variant="outline" data-testid="rfi-link-add" onClick={addLink}><LinkSimple size={14} className="mr-1" />Add link</Button>
          </div>
          {(rfi.attachments || []).length === 0 ? (
            <div className="text-xs text-muted-foreground">No files or links attached.</div>
          ) : (
            <ul className="space-y-1">
              {rfi.attachments.map((a) => (
                <li key={a.id} className="flex items-center justify-between text-sm">
                  {a.type === "link" ? (
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline flex items-center gap-1 min-w-0" data-testid={`rfi-att-${a.id}`}>
                      <LinkSimple size={14} className="shrink-0" /> <span className="truncate">{a.url}</span>
                    </a>
                  ) : (
                    <a href={`${API}/rfis/${rfiId}/attachments/${a.id}/download`} className="text-blue-700 hover:underline flex items-center gap-1" data-testid={`rfi-att-${a.id}`}>
                      <DownloadSimple size={14} /> {a.filename}
                    </a>
                  )}
                  <button className="text-red-600 shrink-0 ml-2" data-testid={`rfi-att-del-${a.id}`} onClick={() => delAtt(a.id)}><Trash size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* workflow actions */}
        {canEdit && (
          <div className="border border-border p-3 bg-secondary/40">
            <div className="overline mb-2 flex items-center gap-1"><FlagBanner size={14} /> Workflow — currently {stageLabel(status)}</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Input data-testid="rfi-action-assignee" value={actAssignee} onChange={(e) => setActAssignee(e.target.value)} placeholder="Assign next owner (optional)" />
              <Input data-testid="rfi-action-comment" value={actComment} onChange={(e) => setActComment(e.target.value)} placeholder="Comment (optional)" />
            </div>
            <div className="flex flex-wrap gap-2">
              {status === "draft" && <Button size="sm" data-testid="rfi-action-start" onClick={() => doAction("start")}><ArrowRight size={14} className="mr-1" />Start (send to SME)</Button>}
              {status !== "draft" && status !== "compliance_final" && canForward && (
                <Button size="sm" data-testid="rfi-action-forward" onClick={() => doAction("forward")}><ArrowRight size={14} className="mr-1" />Forward to next stage</Button>
              )}
              {status === "compliance_final" && <Button size="sm" className="bg-green-700 hover:bg-green-800" data-testid="rfi-action-complete" onClick={() => doAction("complete")}><CheckCircle size={14} className="mr-1" />Mark complete</Button>}
              {canReturn && <Button size="sm" variant="outline" data-testid="rfi-action-return" onClick={() => doAction("return")}><ArrowLeft size={14} className="mr-1" />Return to previous</Button>}
            </div>
          </div>
        )}

        {(rfi.history || []).length > 0 && (
          <div className="border border-border p-3">
            <div className="overline mb-2 flex items-center gap-1"><ClockCounterClockwise size={14} /> History</div>
            <ul className="space-y-1.5">
              {[...rfi.history].reverse().map((h, i) => (
                <li key={`${h.at || ""}-${i}`} className="text-xs text-muted-foreground flex flex-wrap gap-1">
                  <span className="font-semibold text-foreground">{h.by}</span>
                  <span>{h.action}: {stageLabel(h.from_status)} → {stageLabel(h.to_status)}</span>
                  <span>· {new Date(h.at).toLocaleString()}</span>
                  {h.comment && <span className="italic">— “{h.comment}”</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {canEdit && (
          <div className="flex justify-end pt-2">
            <Button data-testid="rfi-save" onClick={save}><FloppyDisk size={16} className="mr-1" />Save</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- RFI List --------------------------------- */
function RfiList({ standards }) {
  const { isRole } = useAuth();
  const canEdit = isRole("admin") || isRole("analyst") || isRole("reviewer");
  const [rfis, setRfis] = useState([]);
  const [summary, setSummary] = useState({ by_status: {}, total: 0 });
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/rfis").then((r) => setRfis(r.data)).catch(() => {});
    api.get("/rfis/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = filter ? rfis.filter((r) => r.status === filter) : rfis;
  const delRfi = async (id) => {
    if (!window.confirm("Delete this RFI item?")) return;
    try { await api.delete(`/rfis/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      {canEdit && <IntakePanel standards={standards} onCreated={load} />}

      <div className="flex flex-wrap gap-2" data-testid="rfi-status-filters">
        <button onClick={() => setFilter("")} data-testid="rfi-filter-all"
          className={`px-3 py-1 text-xs font-semibold border ${filter === "" ? "bg-primary text-primary-foreground border-black" : "bg-card border-border"}`}>
          All ({summary.total})
        </button>
        {STAGES.map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`rfi-filter-${s.key}`}
            className={`px-3 py-1 text-xs font-semibold border ${filter === s.key ? "bg-primary text-primary-foreground border-black" : "bg-card border-border"}`}>
            {s.label} ({summary.by_status?.[s.key] || 0})
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Empty title="No RFIs yet" hint="Paste questions from NERC Align or upload a Word document above to get started." />
      ) : (
        <div className="border border-border bg-card divide-y" data-testid="rfi-list">
          {shown.map((r) => (
            <div key={r.id} className="flex items-start gap-3 px-4 py-3 hover:bg-secondary/40 cursor-pointer"
              data-testid={`rfi-row-${r.id}`} onClick={() => setOpenId(r.id)}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <StageBadge status={r.status} />
                  {r.sel_number && <span className="text-[11px] font-mono bg-secondary px-1.5 py-0.5 border border-gray-200">{r.sel_number}</span>}
                  {r.standard_id && <span className="text-[11px] font-semibold text-foreground">{r.standard_id} {r.req_part}</span>}
                </div>
                <div className="text-sm mt-1 truncate">{r.question}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {r.assigned_sme || r.assigned_compliance || r.assigned_other_legal
                    ? `SME: ${r.assigned_sme || "—"} · Compliance: ${r.assigned_compliance || "—"} · Other/Legal: ${r.assigned_other_legal || "—"}`
                    : "Unassigned"}
                  {(r.attachments || []).length > 0 && ` · ${r.attachments.length} file(s)`}
                </div>
              </div>
              {canEdit && (
                <button data-testid={`rfi-del-${r.id}`} onClick={(e) => { e.stopPropagation(); delRfi(r.id); }}
                  className="text-gray-400 hover:text-red-600 shrink-0"><Trash size={16} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {openId && <RfiDetail rfiId={openId} standards={standards} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

/* ------------------------------ Question Bank ------------------------------ */
function QuestionBank({ onCreatedRfis }) {
  const { isRole } = useAuth();
  const canEdit = isRole("admin") || isRole("analyst") || isRole("reviewer");
  const [meta, setMeta] = useState({ standards: [], total: 0 });
  const [std, setStd] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [sel, setSel] = useState("");
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadMeta = useCallback(() => {
    api.get("/rfi-bank/standards").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const search = useCallback(() => {
    setLoading(true);
    const params = {};
    if (std !== "all") params.standard_id = std;
    if (q.trim()) params.q = q.trim();
    params.limit = 300;
    api.get("/rfi-bank", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [std, q]);
  useEffect(() => { search(); }, [search]);

  const seed = async () => {
    setSeeding(true);
    try { const { data } = await api.post("/rfi-bank/seed"); toast.success(`${data.count} questions available`); loadMeta(); search(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSeeding(false); }
  };

  const selCount = Object.values(selected).filter(Boolean).length;
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const createRfis = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast.error("Select at least one question");
    try {
      const { data } = await api.post("/rfis/from-bank", { bank_ids: ids, sel_number: sel });
      toast.success(`${data.created} RFI item(s) created`);
      setSelected({}); setSel("");
      onCreatedRfis();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (meta.total === 0) {
    return (
      <Empty title="Question Bank is empty"
        hint={canEdit ? "Load the bundled NERC Engagement Common Questions library to begin." : "Ask an admin to load the question library."} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Label className="text-xs">Standard</Label>
          <Select value={std} onValueChange={setStd}>
            <SelectTrigger data-testid="bank-standard"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All standards</SelectItem>
              {meta.standards.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-2 top-2.5 text-muted-foreground" />
            <Input data-testid="bank-search" className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search question text…" />
          </div>
        </div>
        <div className="text-xs text-muted-foreground pb-2">{meta.total} questions in library</div>
      </div>

      {canEdit && selCount > 0 && (
        <div className="flex items-center gap-3 border border-blue-200 bg-blue-50 p-3" data-testid="bank-selection-bar">
          <span className="text-sm font-semibold">{selCount} selected</span>
          <Input data-testid="bank-sel-number" className="w-48" value={sel} onChange={(e) => setSel(e.target.value)} placeholder="SEL # (optional)" />
          <Button size="sm" data-testid="bank-create-rfis" onClick={createRfis}><Plus size={14} className="mr-1" />Create RFI item(s)</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected({})}>Clear</Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <Empty title="No matching questions" hint="Try a different standard or search term." />
      ) : (
        <div className="border border-border bg-card divide-y" data-testid="bank-list">
          {items.map((it) => (
            <label key={it.id} className="flex items-start gap-3 px-4 py-2.5 hover:bg-secondary/40 cursor-pointer" data-testid={`bank-row-${it.id}`}>
              {canEdit && (
                <input type="checkbox" className="mt-1" checked={!!selected[it.id]} onChange={() => toggle(it.id)} data-testid={`bank-check-${it.id}`} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {it.standard_id && <span className="text-[11px] font-semibold text-foreground">{it.standard_id}{it.req_part ? ` ${it.req_part}` : ""}</span>}
                  {it.category && <span className="text-[10px] bg-secondary px-1.5 py-0.5 border border-gray-200 uppercase">{it.category}</span>}
                </div>
                <div className="text-sm mt-0.5">{it.question}</div>
                {it.notes && <div className="text-xs text-muted-foreground mt-0.5 italic">{it.notes}</div>}
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- Page ----------------------------------- */
export default function Rfi() {
  const [standards, setStandards] = useState([]);
  const [tab, setTab] = useState("rfis");
  const [bankReady, setBankReady] = useState(true);
  const { isRole } = useAuth();

  useEffect(() => {
    api.get("/standards").then((r) => setStandards(r.data)).catch(() => {});
    api.get("/rfi-bank/standards").then((r) => setBankReady(r.data.total > 0)).catch(() => {});
  }, []);

  const seedBank = async () => {
    try { const { data } = await api.post("/rfi-bank/seed"); toast.success(`${data.count} questions loaded`); setBankReady(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="RFI Workspace"
        subtitle="Manage NERC Requests for Information — SME → Compliance → Other/Legal → Compliance"
        action={!bankReady && (isRole("admin") || isRole("analyst")) ? (
          <Button data-testid="rfi-seed-bank" onClick={seedBank}><DownloadSimple size={16} className="mr-1" />Load Question Bank</Button>
        ) : null} />
      <div className="p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="rfis" data-testid="rfi-tab-rfis">RFIs</TabsTrigger>
            <TabsTrigger value="bank" data-testid="rfi-tab-bank">Question Bank</TabsTrigger>
          </TabsList>
          <TabsContent value="rfis"><RfiList standards={standards} /></TabsContent>
          <TabsContent value="bank"><QuestionBank onCreatedRfis={() => setTab("rfis")} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
